import { KeyboardEvent, useCallback, useContext } from 'react'

import { KEYS } from 'core-components/helpers/eventKeys'
import { PortalContext } from 'core-components/library/popup'

import { getTabbableElements } from '../utils/getTabbableElements'

/**
 * Keeps the tab sequence inside the dialog: the last element wraps to the first one and back,
 * so keyboard navigation can not reach the page behind the dialog.
 *
 * Returns a keydown handler for the dialog window, which is the trapping container itself.
 */
export const useFocusTrap = () => {
  const { rootNode } = useContext(PortalContext)

  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== KEYS.tab || event.nativeEvent.defaultPrevented) {
        return
      }

      const container = event.currentTarget
      const focusedElement = rootNode.activeElement
      const tabbableElements = getTabbableElements(container)

      if (!tabbableElements.length) {
        event.preventDefault()
        container.focus()
        return
      }

      const firstElement = tabbableElements[0]
      const lastElement = tabbableElements[tabbableElements.length - 1]

      // the dialog itself is focused right after opening: forward navigation already steps
      // into the dialog content, only the backward one leaves it
      if (focusedElement === container) {
        if (event.shiftKey) {
          event.preventDefault()
          lastElement.focus()
        }
        return
      }

      // focus is inside a popup portalled out of the dialog (select, menu, datepicker),
      // such a popup handles keyboard navigation on its own
      if (!container.contains(focusedElement)) {
        return
      }

      if (event.shiftKey && focusedElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      if (!event.shiftKey && focusedElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },
    [rootNode]
  )
}
